import { create } from 'zustand';
import { supabase, isSupabaseConfigured } from '../lib/supabase';
import type { 
  Message, 
  Conversation, 
  MessageThread, 
  CreateMessageForm, 
  MessageFilters 
} from '../types/messaging';

interface MessageState {
  messages: Message[];
  conversations: Conversation[];
  currentThread: MessageThread | null;
  unreadCount: number;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  fetchMessages: (filters?: MessageFilters) => Promise<void>;
  fetchConversations: () => Promise<void>;
  fetchMessageThread: (participantId: string) => Promise<void>;
  sendMessage: (messageData: CreateMessageForm) => Promise<void>;
  replyToMessage: (messageId: string, content: string) => Promise<void>;
  markAsRead: (messageId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteMessage: (messageId: string) => Promise<void>;
  
  // Utilities
  clearError: () => void;
  setCurrentThread: (thread: MessageThread | null) => void;
}

// Mock data for development
const MOCK_MESSAGES: Message[] = [
  {
    id: '1',
    sender_id: 'coord-1',
    recipient_id: 'porteur-1',
    subject: 'Bienvenue dans l\'accompagnement ESS !',
    content: 'Bonjour Marie,\n\nJe suis Sophie Martin, votre accompagnatrice pour le projet "Ferme Agroécologique des Antilles".\n\nJe vous félicite pour votre initiative dans l\'Économie Sociale et Solidaire ! Je serai à vos côtés tout au long de ce parcours.\n\nCordialement,\nSophie',
    is_read: true,
    created_at: '2024-01-15T09:00:00Z',
    sender: {
      id: 'coord-1',
      full_name: 'Sophie Martin',
      email: 'sophie.martin@cress-guadeloupe.org',
      profile_type: 'ACCOMPAGNATEUR',
      organization: 'CRESS Guadeloupe'
    }
  },
  {
    id: '2',
    sender_id: 'porteur-1',
    recipient_id: 'coord-1',
    subject: 'Re: Bienvenue dans l\'accompagnement ESS !',
    content: 'Bonjour Sophie,\n\nMerci beaucoup pour votre message de bienvenue ! Je suis très motivée pour ce projet.\n\nJ\'ai quelques questions sur les documents à fournir. Pouvons-nous programmer un rendez-vous ?\n\nCordialement,\nMarie',
    is_read: false,
    parent_message_id: '1',
    created_at: '2024-01-15T14:30:00Z',
    sender: {
      id: 'porteur-1',
      full_name: 'Marie Joseph',
      email: 'marie.joseph@test.com',
      profile_type: 'PORTEUR',
      organization: 'Coopérative Agricole Gwada'
    }
  },
  {
    id: '3',
    sender_id: 'coord-2',
    recipient_id: 'porteur-1',
    subject: 'Documents manquants pour votre projet',
    content: 'Bonjour Marie,\n\nJ\'ai examiné votre dossier et il manque quelques documents :\n- KBIS récent\n- RIB\n\nMerci de les téléverser avant le 25 janvier.\n\nCordialement,\nJean-Claude',
    is_read: false,
    created_at: '2024-01-14T16:45:00Z',
    sender: {
      id: 'coord-2',
      full_name: 'Jean-Claude Martin',
      email: 'coordination@cress-guadeloupe.org',
      profile_type: 'COORDINATEUR',
      organization: 'CRESS Guadeloupe'
    }
  }
];

export const useMessageStore = create<MessageState>((set, get) => ({
  messages: [],
  conversations: [],
  currentThread: null,
  unreadCount: 0,
  isLoading: false,
  error: null,

  fetchMessages: async (filters?: MessageFilters) => {
    set({ isLoading: true, error: null });
    try {
      if (!isSupabaseConfigured()) {
        // Use mock data
        let filteredMessages = MOCK_MESSAGES;
        
        if (filters?.search) {
          const searchLower = filters.search.toLowerCase();
          filteredMessages = filteredMessages.filter(m => 
            m.subject.toLowerCase().includes(searchLower) ||
            m.content.toLowerCase().includes(searchLower) ||
            m.sender?.full_name?.toLowerCase().includes(searchLower)
          );
        }
        
        if (filters?.unread_only) {
          filteredMessages = filteredMessages.filter(m => !m.is_read);
        }
        
        if (filters?.sender_id) {
          filteredMessages = filteredMessages.filter(m => m.sender_id === filters.sender_id);
        }
        
        const unreadCount = filteredMessages.filter(m => !m.is_read).length;
        set({ messages: filteredMessages, unreadCount, isLoading: false });
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      let query = supabase
        .from('messages')
        .select(`
          *,
          sender:users!messages_sender_id_fkey(*),
          recipient:users!messages_recipient_id_fkey(*)
        `)
        .eq('recipient_id', user.id)
        .order('created_at', { ascending: false });

      if (filters?.search) {
        query = query.or(`subject.ilike.%${filters.search}%,content.ilike.%${filters.search}%`);
      }
      if (filters?.unread_only) {
        query = query.eq('is_read', false);
      }
      if (filters?.sender_id) {
        query = query.eq('sender_id', filters.sender_id);
      }

      const { data, error } = await query;
      if (error) throw error;

      const unreadCount = data?.filter(m => !m.is_read).length || 0;
      set({ messages: data || [], unreadCount, isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  fetchConversations: async () => {
    set({ isLoading: true, error: null });
    try {
      if (!isSupabaseConfigured()) {
        // Mock conversations
        const conversations: Conversation[] = [
          {
            id: '1',
            participants: ['coord-1', 'porteur-1'],
            last_message: MOCK_MESSAGES[1],
            unread_count: 1,
            created_at: '2024-01-15T09:00:00Z',
            updated_at: '2024-01-15T14:30:00Z'
          },
          {
            id: '2',
            participants: ['coord-2', 'porteur-1'],
            last_message: MOCK_MESSAGES[2],
            unread_count: 1,
            created_at: '2024-01-14T16:45:00Z',
            updated_at: '2024-01-14T16:45:00Z'
          }
        ];
        set({ conversations, isLoading: false });
        return;
      }

      // TODO: Implement real conversation fetching
      set({ conversations: [], isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  fetchMessageThread: async (participantId: string) => {
    set({ isLoading: true, error: null });
    try {
      if (!isSupabaseConfigured()) {
        // Mock thread
        const threadMessages = MOCK_MESSAGES.filter(m => 
          (m.sender_id === participantId || m.recipient_id === participantId)
        );
        
        const participants = [
          {
            id: 'coord-1',
            full_name: 'Sophie Martin',
            email: 'sophie.martin@cress-guadeloupe.org',
            profile_type: 'ACCOMPAGNATEUR',
            organization: 'CRESS Guadeloupe'
          },
          {
            id: 'porteur-1',
            full_name: 'Marie Joseph',
            email: 'marie.joseph@test.com',
            profile_type: 'PORTEUR',
            organization: 'Coopérative Agricole Gwada'
          }
        ];
        
        set({ 
          currentThread: { messages: threadMessages, participants },
          isLoading: false 
        });
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      // Fetch messages between current user and participant
      const { data: messages, error } = await supabase
        .from('messages')
        .select(`
          *,
          sender:users!messages_sender_id_fkey(*),
          recipient:users!messages_recipient_id_fkey(*)
        `)
        .or(`and(sender_id.eq.${user.id},recipient_id.eq.${participantId}),and(sender_id.eq.${participantId},recipient_id.eq.${user.id})`)
        .order('created_at', { ascending: true });

      if (error) throw error;

      // Fetch participants info
      const { data: participants, error: participantsError } = await supabase
        .from('users')
        .select('*')
        .in('id', [user.id, participantId]);

      if (participantsError) throw participantsError;

      set({ 
        currentThread: { 
          messages: messages || [], 
          participants: participants || [] 
        },
        isLoading: false 
      });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
    }
  },

  sendMessage: async (messageData: CreateMessageForm) => {
    set({ isLoading: true, error: null });
    try {
      if (!isSupabaseConfigured()) {
        // Mock sending
        const newMessage: Message = {
          id: Date.now().toString(),
          sender_id: 'current-user',
          recipient_id: messageData.recipient_id,
          subject: messageData.subject,
          content: messageData.content,
          is_read: false,
          parent_message_id: messageData.parent_message_id,
          created_at: new Date().toISOString(),
          sender: {
            id: 'current-user',
            full_name: 'Utilisateur Actuel',
            email: 'user@example.com',
            profile_type: 'PORTEUR'
          }
        };
        
        const { messages } = get();
        set({ messages: [newMessage, ...messages], isLoading: false });
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('User not authenticated');

      const { data, error } = await supabase
        .from('messages')
        .insert({
          sender_id: user.id,
          recipient_id: messageData.recipient_id,
          subject: messageData.subject,
          content: messageData.content,
          parent_message_id: messageData.parent_message_id
        })
        .select(`
          *,
          sender:users!messages_sender_id_fkey(*),
          recipient:users!messages_recipient_id_fkey(*)
        `)
        .single();

      if (error) throw error;

      // Create notification for recipient
      await supabase
        .from('notifications_enhanced')
        .insert({
          user_id: messageData.recipient_id,
          type: 'MESSAGE_RECEIVED',
          title: 'Nouveau message',
          message: `${data.sender?.full_name || 'Un utilisateur'} vous a envoyé un message`,
          link: `/messages?thread=${user.id}`,
          metadata: {
            message_id: data.id,
            sender_name: data.sender?.full_name || 'Utilisateur',
            subject: messageData.subject
          }
        });

      // Update local state
      const { messages } = get();
      set({ messages: [data, ...messages], isLoading: false });
    } catch (error: any) {
      set({ error: error.message, isLoading: false });
      throw error;
    }
  },

  replyToMessage: async (messageId: string, content: string) => {
    try {
      const { messages } = get();
      const originalMessage = messages.find(m => m.id === messageId);
      if (!originalMessage) throw new Error('Message original non trouvé');

      await get().sendMessage({
        recipient_id: originalMessage.sender_id,
        subject: `Re: ${originalMessage.subject}`,
        content,
        parent_message_id: messageId
      });
    } catch (error: any) {
      set({ error: error.message });
      throw error;
    }
  },

  markAsRead: async (messageId: string) => {
    try {
      if (!isSupabaseConfigured()) {
        const { messages } = get();
        const updatedMessages = messages.map(m => 
          m.id === messageId ? { ...m, is_read: true } : m
        );
        const unreadCount = updatedMessages.filter(m => !m.is_read).length;
        set({ messages: updatedMessages, unreadCount });
        return;
      }

      const { error } = await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('id', messageId);

      if (error) throw error;

      // Update local state
      const { messages } = get();
      const updatedMessages = messages.map(m => 
        m.id === messageId ? { ...m, is_read: true } : m
      );
      const unreadCount = updatedMessages.filter(m => !m.is_read).length;
      set({ messages: updatedMessages, unreadCount });
    } catch (error: any) {
      set({ error: error.message });
    }
  },

  markAllAsRead: async () => {
    try {
      if (!isSupabaseConfigured()) {
        const { messages } = get();
        const updatedMessages = messages.map(m => ({ ...m, is_read: true }));
        set({ messages: updatedMessages, unreadCount: 0 });
        return;
      }

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('messages')
        .update({ is_read: true })
        .eq('recipient_id', user.id)
        .eq('is_read', false);

      if (error) throw error;

      // Update local state
      const { messages } = get();
      const updatedMessages = messages.map(m => ({ ...m, is_read: true }));
      set({ messages: updatedMessages, unreadCount: 0 });
    } catch (error: any) {
      set({ error: error.message });
    }
  },

  deleteMessage: async (messageId: string) => {
    try {
      const { error } = await supabase
        .from('messages')
        .delete()
        .eq('id', messageId);

      if (error) throw error;

      // Update local state
      const { messages } = get();
      const updatedMessages = messages.filter(m => m.id !== messageId);
      const unreadCount = updatedMessages.filter(m => !m.is_read).length;
      set({ messages: updatedMessages, unreadCount });
    } catch (error: any) {
      set({ error: error.message });
    }
  },

  clearError: () => set({ error: null }),
  setCurrentThread: (thread) => set({ currentThread: thread })
}));